import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDate, formatNOK } from "@/lib/format";
import { EQUIPMENT_STATUS_LABELS } from "@/lib/equipment/constants";
import { formatUsage } from "@/lib/equipment/availability";
import type { DBEquipmentItem, DBEquipmentUsage } from "@/lib/db-types";

interface EquipmentFactsCardProps {
  item: DBEquipmentItem;
  categoryName: string;
  departmentName: string;
  bookable: boolean;
  /** Innkjøpspris og forsikringsverdi vises bare for admin og utstyrsansvarlige. */
  showAmounts: boolean;
  /** VIB-2919: «Brukt 12 mnd» fra bookingene. */
  usage?: DBEquipmentUsage;
}

export function EquipmentFactsCard({ item, categoryName, departmentName, bookable, showAmounts, usage }: EquipmentFactsCardProps) {
  const insured = item.insured_until
    ? `${formatDate(item.insured_until)}${showAmounts && item.insurance_value != null ? ` · ${formatNOK(item.insurance_value)}` : ""}`
    : item.insurance_declined
      ? "Ikke forsikret (ikke forny)"
      : "Ikke forsikret";
  const facts: [string, string][] = [
    ["Kategori", categoryName],
    ["Avdeling", departmentName],
    ["Antall", String(item.quantity)],
    ["Status", EQUIPMENT_STATUS_LABELS[item.status]],
    ["Kan bookes", bookable ? "Ja" : "Nei"],
    ["Brukt 12 mnd", formatUsage(usage)],
    ["Forsikret til", insured],
    ...(showAmounts
      ? ([["Innkjøpspris", item.purchase_price != null ? `${formatNOK(item.purchase_price)} eks. mva` : "–"]] as [string, string][])
      : []),
    ["Innkjøpsdato", item.purchase_date ? formatDate(item.purchase_date) : "–"],
    ["Leiepris per døgn", item.rental_price_day != null ? formatNOK(item.rental_price_day) : "–"],
    ["Kilde", item.source ?? "–"],
  ];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Fakta</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            {facts.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Deler og tilbehør</CardTitle>
          <span className="text-xs text-muted-foreground">{item.parts.length}</span>
        </CardHeader>
        <CardContent>
          {item.parts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen registrert.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5" aria-label="Deler og tilbehør">
              {item.parts.map((p, i) => (
                <li key={`${p}-${i}`} className="rounded-full border px-2.5 py-0.5 text-xs">
                  {p}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
